/**
 * Global command palette — Cmd+K (or Ctrl+K, or /) opens a centered
 * dialog with fuzzy search across:
 *
 *   • API: customers, licenses, installations (server-side)
 *   • Local: platform-bugs + customer tickets (filtered client-side)
 *   • Recent visits — sessionStorage-backed list of pages last visited
 *   • Page navigation — typing "customers" surfaces the route too
 *
 * Escape closes; ↑/↓ move; Enter navigates.
 *
 * `recordVisit(pathname, label)` is exposed so layouts can stamp a
 * page on entry. `<RouteVisitTracker />` wires that up to the current
 * `useLocation()` so each page lands in the recent list automatically.
 */
import {
  useEffect, useMemo, useRef, useState, type JSX,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Search, ArrowRight, Bug, KeyRound, ServerCog, Users, Ticket,
  Clock, CornerDownLeft, Command,
} from 'lucide-react';
import {
  AdminApiClient,
  type SearchResults, type PlatformBugRow, type CustomerTicketRow,
} from '../api';
import { useSession } from '../session';
import { usePermissions } from '../lib/permissions';
import { failureFrom } from '../lib/api-state';

type Hit =
  | { kind: 'page'; id: string; title: string; sub: string; href: string; icon: typeof Search; tone: string }
  | { kind: 'customer'; id: string; title: string; sub: string; href: string }
  | { kind: 'license'; id: string; title: string; sub: string; href: string }
  | { kind: 'installation'; id: string; title: string; sub: string; href: string }
  | { kind: 'bug'; id: string; title: string; sub: string; href: string }
  | { kind: 'ticket'; id: string; title: string; sub: string; href: string }
  | { kind: 'recent'; id: string; title: string; sub: string; href: string };

const PAGES: Array<{ id: string; title: string; sub: string; href: string; icon: typeof Search; tone: string }> = [
  { id: 'p-dash',     title: 'Dashboard',     sub: 'Operations health overview', href: '/',                       icon: Command,  tone: '#818cf8' },
  { id: 'p-cust',     title: 'Customers',     sub: 'Account roster + drawer',     href: '/customers',              icon: Users,    tone: '#c084fc' },
  { id: 'p-lic',      title: 'Licenses',      sub: 'Issue / suspend / revoke',    href: '/licenses',               icon: KeyRound, tone: '#22d3ee' },
  { id: 'p-trial',    title: 'Trials',        sub: 'Trial pipeline + conversion', href: '/trials',                 icon: Clock,    tone: '#fb923c' },
  { id: 'p-srv',      title: 'Servers',       sub: 'Engine fleet + freshness',    href: '/servers',                icon: ServerCog, tone: '#34d399' },
  { id: 'p-bugs',     title: 'Platform bugs', sub: 'Engine crash triage',         href: '/platform-bugs',          icon: Bug,      tone: '#f87171' },
  { id: 'p-tic',      title: 'Tickets',       sub: 'Customer support inbox',      href: '/tickets',                icon: Ticket,   tone: '#22d3ee' },
  { id: 'p-sla',      title: 'SLA dashboard', sub: 'Within / breached / at-risk', href: '/tickets/sla',            icon: Clock,    tone: '#f97316' },
  { id: 'p-anom',     title: 'Anomalies',     sub: 'Heuristic alarms',            href: '/anomalies',              icon: Bug,      tone: '#fbbf24' },
  { id: 'p-cert',     title: 'Customer certs',sub: 'Signing certs',               href: '/customer-certs',         icon: KeyRound, tone: '#34d399' },
  { id: 'p-upd',      title: 'Updates',       sub: 'Engine releases',             href: '/updates',                icon: Command,  tone: '#a78bfa' },
  { id: 'p-pricing',  title: 'Pricing',       sub: 'Plan editor + apply preview', href: '/pricing',                icon: KeyRound, tone: '#22d3ee' },
  { id: 'p-mkt',      title: 'Marketplace',   sub: 'AWS / GCP entitlements',      href: '/marketplace-entitlements', icon: Command, tone: '#fb923c' },
  { id: 'p-funnel',   title: 'Funnel',        sub: 'Signup → paid stages',        href: '/funnel',                 icon: Command,  tone: '#fb923c' },
  { id: 'p-ana',      title: 'Analytics',     sub: 'Trends + distributions',      href: '/analytics',              icon: Command,  tone: '#818cf8' },
  { id: 'p-audit',    title: 'Audit log',     sub: 'Staff + auto-provisioning',   href: '/audit-log',              icon: Clock,    tone: '#94a3b8' },
  { id: 'p-staff',    title: 'Staff',         sub: 'Roster + roles + MFA',        href: '/staff',                  icon: Users,    tone: '#a78bfa' },
];

const RECENT_KEY = 'vloud:recent_visits';
const MAX_RECENT = 6;

interface RecentVisit { href: string; title: string; at: number }

function readRecent(): RecentVisit[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as RecentVisit[];
  } catch { return []; }
}
function writeRecent(visits: RecentVisit[]): void {
  if (typeof window === 'undefined') return;
  try { sessionStorage.setItem(RECENT_KEY, JSON.stringify(visits)); }
  catch { /* ignore */ }
}

export function recordVisit(href: string, title: string): void {
  if (!title || href === '/signin') return;
  const list = readRecent().filter((v) => v.href !== href);
  list.unshift({ href, title, at: Date.now() });
  writeRecent(list.slice(0, MAX_RECENT));
}

/* ──────────────────────────────────────────────────────────────────
 * Visit tracker — drop into ProtectedLayout so navigation auto-stamps
 * recent visits.
 * ────────────────────────────────────────────────────────────────── */
export function RouteVisitTracker(): null {
  const loc = useLocation();
  useEffect(() => {
    const page = PAGES.find((p) => p.href === loc.pathname);
    if (page) recordVisit(page.href, page.title);
  }, [loc.pathname]);
  return null;
}

/** Show only enough of an activation key to recognise it in a result list. */
function maskKey(key: string): string {
  if (key.startsWith('****')) return key;          // server already masked it for this role
  return key.length > 8 ? `${key.slice(0, 4)}…${key.slice(-2)}` : key;
}

export function GlobalSearch(): JSX.Element {
  const { state } = useSession();
  const perms = usePermissions();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [bugs, setBugs] = useState<PlatformBugRow[]>([]);
  const [tickets, setTickets] = useState<CustomerTicketRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Open on Cmd/Ctrl+K or `/` (when not typing in an input).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const editable = target?.isContentEditable
                    || tag === 'input' || tag === 'textarea' || tag === 'select';

      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (e.key === '/' && !editable && !open) {
        e.preventDefault();
        setOpen(true);
        return;
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Focus input when palette opens.
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      // Reset state on close.
      setQ('');
      setResults(null);
      setActiveIdx(0);
    }
  }, [open]);

  // Fetch palette payloads when the query changes (debounced).
  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(() => {
      const api = new AdminApiClient({ baseUrl: '', getToken: () => state.token });
      const trimmed = q.trim();
      // Server search needs ≥2 chars; bugs/tickets fetch unconditionally
      // so the first open already shows real entries.
      const tasks: Array<Promise<unknown>> = [];
      // /internal/search is owner/admin only on the server.
      if (trimmed.length >= 2 && perms.canReadAdminData) {
        tasks.push(
          api.search(trimmed)
            .then((r) => { setResults(r); setErr(null); })
            .catch((e) => { setResults(null); setErr(failureFrom(e, 'search results').message); }),
        );
      } else {
        setResults(null);
      }
      tasks.push(
        api.platformBugs()
          .then((r) => setBugs(r.platform_bugs))
          .catch(() => setBugs([])),
      );
      tasks.push(
        api.customerTickets('open_or_awaiting')
          .then((r) => setTickets(r.tickets))
          .catch(() => setTickets([])),
      );
      void Promise.all(tasks);
    }, 200);
    return () => clearTimeout(handle);
  }, [q, open, state.token, perms.canReadAdminData]);

  const hits = useMemo<Hit[]>(() => {
    const term = q.trim().toLowerCase();
    const out: Hit[] = [];

    if (term) {
      // Page matches first (zero-network, instant).
      for (const p of PAGES) {
        if (p.title.toLowerCase().includes(term)
         || p.sub.toLowerCase().includes(term)
         || p.href.toLowerCase().includes(term.replace(/^\//, ''))) {
          out.push({ kind: 'page', id: p.id, title: p.title, sub: p.sub, href: p.href, icon: p.icon, tone: p.tone });
        }
      }
    }

    if (results) {
      for (const c of results.customers.slice(0, 6)) {
        out.push({ kind: 'customer', id: c.id, title: c.company_name, sub: c.primary_email, href: `/customers?focus=${c.id}` });
      }
      for (const l of results.licenses.slice(0, 6)) {
        out.push({ kind: 'license', id: l.id, title: maskKey(l.activation_key), sub: `${l.plan_tier} · ${l.status}`, href: `/licenses?focus=${l.id}` });
      }
      for (const i of results.installations.slice(0, 6)) {
        out.push({ kind: 'installation', id: i.id, title: i.machine_fingerprint, sub: `${i.os_platform} · ${i.status}`, href: `/servers?focus=${i.id}` });
      }
    }

    // Local fuzzy match for bugs + tickets.
    if (term) {
      for (const b of bugs) {
        if (b.summary.toLowerCase().includes(term) || b.binary_version.toLowerCase().includes(term)) {
          out.push({
            kind: 'bug', id: b.id,
            title: b.summary, sub: `${b.binary_version} · ${b.occurrences} hits · ${b.status}`,
            href: '/platform-bugs',
          });
          if (out.filter((h) => h.kind === 'bug').length >= 6) break;
        }
      }
      for (const t of tickets) {
        if (t.subject.toLowerCase().includes(term) || t.body.toLowerCase().includes(term)) {
          out.push({
            kind: 'ticket', id: t.id,
            title: t.subject, sub: `${t.priority} · ${t.status.replace(/_/g, ' ')}`,
            href: '/tickets',
          });
          if (out.filter((h) => h.kind === 'ticket').length >= 6) break;
        }
      }
    }

    if (!term) {
      const recents = readRecent();
      for (const r of recents) {
        out.push({ kind: 'recent', id: `recent-${r.href}`, title: r.title, sub: timeAgo(r.at), href: r.href });
      }
      if (out.length === 0) {
        // No recents either → surface the canonical pages so the
        // palette is useful on first open.
        for (const p of PAGES.slice(0, 8)) {
          out.push({ kind: 'page', id: p.id, title: p.title, sub: p.sub, href: p.href, icon: p.icon, tone: p.tone });
        }
      }
    }

    return out;
  }, [q, results, bugs, tickets]);

  // Reset cursor when results change.
  useEffect(() => { setActiveIdx(0); }, [hits.length, q]);

  const go = (href: string): void => {
    setOpen(false);
    nav(href);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, hits.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const hit = hits[activeIdx];
      if (hit) go(hit.href);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open command palette"
        style={{
          width: '100%',
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 12px',
          borderRadius: 10,
          background: 'var(--bg-overlay)',
          border: '1px solid var(--border)',
          color: 'var(--text-muted)',
          fontSize: '0.8125rem',
          cursor: 'text',
        }}
      >
        <Search width={14} height={14} />
        <span style={{ flex: 1, minWidth: 0, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Search customers, licenses, bugs, tickets…
        </span>
        <kbd
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 2,
            padding: '2px 6px', borderRadius: 6,
            background: 'var(--bg-raised)',
            border: '1px solid var(--border)',
            fontSize: '0.6875rem',
            fontFamily: 'ui-monospace, monospace',
            color: 'var(--text-muted)',
          }}
        >
          ⌘K
        </kbd>
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
          style={{
            position: 'fixed', inset: 0, zIndex: 70,
            display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
            paddingTop: 'min(12vh, 120px)',
          }}
        >
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'absolute', inset: 0,
              background: 'rgba(11, 15, 25, 0.6)',
              backdropFilter: 'blur(4px)',
            }}
          />
          <div
            style={{
              position: 'relative',
              width: 'min(620px, 100%)',
              margin: '0 16px',
              borderRadius: 14,
              background: 'var(--bg-raised)',
              border: '1px solid var(--border)',
              boxShadow: '0 24px 60px rgba(0, 0, 0, 0.45)',
              display: 'flex', flexDirection: 'column',
              maxHeight: '70vh',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <Search width={16} height={16} color="var(--text-muted)" />
              <input
                ref={inputRef}
                aria-label="Search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Type to search… (try a bug summary, customer email, or page name)"
                style={{
                  flex: 1,
                  background: 'transparent', border: 'none', outline: 'none',
                  fontSize: '0.9375rem',
                  color: 'var(--text-primary)',
                  fontFamily: 'inherit',
                  padding: 0,
                }}
              />
              <kbd
                style={{
                  fontSize: '0.625rem', color: 'var(--text-muted)',
                  padding: '1px 6px', borderRadius: 4,
                  border: '1px solid var(--border)', background: 'var(--bg-overlay)',
                }}
              >
                Esc
              </kbd>
            </div>

            <div style={{ overflowY: 'auto', padding: 6 }}>
              {err && <p className="err" role="alert" style={{ margin: 8, fontSize: '0.75rem' }}>{err}</p>}
              {!perms.canReadAdminData && (
                <p role="note" style={{ margin: 8, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  Searching customers and licenses is available to Owner and Admin accounts. Pages, engine bugs and tickets are still searchable.
                </p>
              )}
              {hits.length === 0 ? (
                <p
                  style={{
                    color: 'var(--text-muted)',
                    padding: 16,
                    fontSize: '0.8125rem',
                    textAlign: 'center',
                  }}
                >
                  {q.trim().length >= 2 ? 'No matches.' : 'Start typing to search.'}
                </p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {hits.map((h, i) => (
                    <li key={`${h.kind}-${h.id}`}>
                      <Row
                        hit={h}
                        active={i === activeIdx}
                        onHover={() => setActiveIdx(i)}
                        onPick={() => go(h.href)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '8px 14px',
                borderTop: '1px solid var(--border)',
                background: 'var(--bg-overlay)',
                fontSize: '0.6875rem', color: 'var(--text-muted)',
              }}
            >
              <KbdRow label="↑↓" what="navigate" />
              <KbdRow label="↵"  what="open" />
              <KbdRow label="Esc" what="close" />
              <span style={{ marginLeft: 'auto' }}>{hits.length} result{hits.length === 1 ? '' : 's'}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Row({
  hit, active, onHover, onPick,
}: { hit: Hit; active: boolean; onHover: () => void; onPick: () => void }): JSX.Element {
  const Icon = iconForKind(hit);
  const tone = toneForKind(hit);
  const groupLabel = labelForKind(hit);
  return (
    <button
      type="button"
      onClick={onPick}
      onMouseEnter={onHover}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%',
        padding: '8px 10px',
        border: 'none',
        borderRadius: 8,
        background: active ? 'rgba(99, 102, 241, 0.10)' : 'transparent',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span
        aria-hidden
        style={{
          flex: 'none',
          width: 26, height: 26, borderRadius: 6,
          background: `${tone}1f`,
          color: tone,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon width={13} height={13} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: '0.8125rem', fontWeight: 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {hit.title}
        </div>
        <div
          style={{
            fontSize: '0.6875rem', color: 'var(--text-muted)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {hit.sub}
        </div>
      </div>
      <span
        style={{
          fontSize: '0.625rem',
          textTransform: 'uppercase', letterSpacing: '0.04em',
          color: 'var(--text-muted)',
          padding: '2px 6px',
          borderRadius: 4,
          background: 'var(--bg-overlay)',
          border: '1px solid var(--border)',
        }}
      >
        {groupLabel}
      </span>
      {active && <ArrowRight width={12} height={12} color="var(--text-muted)" />}
    </button>
  );
}

function iconForKind(hit: Hit): typeof Search {
  switch (hit.kind) {
    case 'page':         return hit.icon;
    case 'customer':     return Users;
    case 'license':      return KeyRound;
    case 'installation': return ServerCog;
    case 'bug':          return Bug;
    case 'ticket':       return Ticket;
    case 'recent':       return Clock;
  }
}
function toneForKind(hit: Hit): string {
  switch (hit.kind) {
    case 'page':         return hit.tone;
    case 'customer':     return '#c084fc';
    case 'license':      return '#22d3ee';
    case 'installation': return '#34d399';
    case 'bug':          return '#f87171';
    case 'ticket':       return '#22d3ee';
    case 'recent':       return '#94a3b8';
  }
}
function labelForKind(hit: Hit): string {
  switch (hit.kind) {
    case 'page':         return 'Page';
    case 'customer':     return 'Customer';
    case 'license':      return 'License';
    case 'installation': return 'Server';
    case 'bug':          return 'Bug';
    case 'ticket':       return 'Ticket';
    case 'recent':       return 'Recent';
  }
}

function timeAgo(t: number): string {
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function KbdRow({ label, what }: { label: string; what: string }): JSX.Element {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <kbd
        style={{
          fontSize: '0.625rem',
          padding: '1px 5px',
          borderRadius: 4,
          background: 'var(--bg-raised)',
          border: '1px solid var(--border)',
          fontFamily: 'inherit',
          color: 'var(--text-primary)',
        }}
      >
        {label}
      </kbd>
      <CornerDownLeft width={10} height={10} color="transparent" />
      <span>{what}</span>
    </span>
  );
}
